import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  HelpCircle,
  MessageSquare,
  Phone,
  Mail,
  MapPin,
  Clock,
  FileText,
  Send,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [faqItems, setFaqItems] = useState([]);
  const [faqCategories, setFaqCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [contactInfo, setContactInfo] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  
  // Contact form
  const [contactForm, setContactForm] = useState({
    subject: '',
    category: 'general',
    message: '',
    related_loan_reference: ''
  });
  
  // Document request form
  const [docRequestForm, setDocRequestForm] = useState({
    document_type: '',
    description: '',
    related_loan_reference: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [faqRes, categoriesRes, contactRes, ticketsRes] = await Promise.all([
        axios.get(`${API_URL}/api/support/faq`, { headers }),
        axios.get(`${API_URL}/api/support/faq/categories`, { headers }),
        axios.get(`${API_URL}/api/support/contact-info`, { headers }),
        axios.get(`${API_URL}/api/support/tickets`, { headers })
      ]);
      
      setFaqItems(faqRes.data);
      setFaqCategories(categoriesRes.data);
      setContactInfo(contactRes.data);
      setMyTickets(ticketsRes.data);
    } catch (error) {
      console.error('Error fetching support data:', error);
      toast.error('Failed to load support information');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!contactForm.subject || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API_URL}/api/support/tickets`, contactForm, { headers });
      toast.success(`Ticket submitted! Expected response: ${response.data.expected_response}`);
      setContactForm({ subject: '', category: 'general', message: '', related_loan_reference: '' });
      fetchData();
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error('Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitDocRequest = async (e) => {
    e.preventDefault();
    if (!docRequestForm.document_type || !docRequestForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API_URL}/api/support/document-request`, docRequestForm, { headers });
      toast.success(`Document request submitted! Expected response: ${response.data.expected_response}`);
      setDocRequestForm({ document_type: '', description: '', related_loan_reference: '' });
      fetchData();
    } catch (error) {
      console.error('Error submitting document request:', error);
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredFaq = selectedCategory === 'all' 
    ? faqItems 
    : faqItems.filter(item => item.category === selectedCategory);

  const getStatusBadge = (status) => {
    const config = {
      open: { color: 'bg-blue-100 text-blue-800', label: 'Open' },
      in_progress: { color: 'bg-yellow-100 text-yellow-800', label: 'In Progress' },
      resolved: { color: 'bg-green-100 text-green-800', label: 'Resolved' },
      closed: { color: 'bg-gray-100 text-gray-800', label: 'Closed' }
    };
    const statusConfig = config[status] || config.open;
    return <Badge className={statusConfig.color}>{statusConfig.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="support-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2"><Skeleton className="h-96 w-full" /></div>
          <div><Skeleton className="h-64 w-full" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#212529] font-['Manrope']">
            Help & Support
          </h1>
          <p className="text-[#6C757D] mt-1">
            Find answers or get in touch with our team
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          className="flex items-center gap-2"
          data-testid="refresh-support-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="faq" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="faq" data-testid="tab-faq">
                <HelpCircle className="h-4 w-4 mr-2" />
                FAQ
              </TabsTrigger>
              <TabsTrigger value="contact" data-testid="tab-contact">
                <MessageSquare className="h-4 w-4 mr-2" />
                Contact Us
              </TabsTrigger>
              <TabsTrigger value="document" data-testid="tab-document">
                <FileText className="h-4 w-4 mr-2" />
                Request Document
              </TabsTrigger>
            </TabsList>

            {/* FAQ Tab */}
            <TabsContent value="faq" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Frequently Asked Questions</CardTitle>
                  <CardDescription>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Button
                        variant={selectedCategory === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedCategory('all')}
                        className={selectedCategory === 'all' ? 'bg-[#1D2D2A]' : ''}
                      >
                        All
                      </Button>
                      {faqCategories.map(cat => (
                        <Button
                          key={cat.id}
                          variant={selectedCategory === cat.id ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedCategory(cat.id)}
                          className={selectedCategory === cat.id ? 'bg-[#1D2D2A]' : ''}
                        >
                          {cat.name}
                        </Button>
                      ))}
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {filteredFaq.map((item) => (
                      <AccordionItem key={item.id} value={item.id}>
                        <AccordionTrigger className="text-left hover:no-underline">
                          <div className="flex items-start gap-3">
                            <HelpCircle className="h-5 w-5 text-[#1D2D2A] flex-shrink-0 mt-0.5" />
                            <span>{item.question}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pl-8 text-gray-600">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  {filteredFaq.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No FAQs found for this category</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Contact Tab */}
            <TabsContent value="contact" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Submit a Support Ticket</CardTitle>
                  <CardDescription>
                    We aim to respond to all enquiries within 24-48 business hours
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitTicket} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="category">Category *</Label>
                        <Select 
                          value={contactForm.category} 
                          onValueChange={(value) => setContactForm(prev => ({ ...prev, category: value }))}
                        >
                          <SelectTrigger data-testid="select-ticket-category">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Enquiry</SelectItem>
                            <SelectItem value="investment">Investment Query</SelectItem>
                            <SelectItem value="distribution">Distribution Query</SelectItem>
                            <SelectItem value="tax">Tax Query</SelectItem>
                            <SelectItem value="kyc">KYC / Compliance</SelectItem>
                            <SelectItem value="technical">Technical Issue</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="related_loan">Related Loan Reference (optional)</Label>
                        <Input
                          id="related_loan"
                          value={contactForm.related_loan_reference}
                          onChange={(e) => setContactForm(prev => ({ ...prev, related_loan_reference: e.target.value }))}
                          placeholder="e.g., PTC-2024-001"
                          data-testid="input-related-loan"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        value={contactForm.subject}
                        onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="Brief description of your enquiry"
                        required
                        data-testid="input-subject"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="Please provide details about your enquiry..."
                        rows={5}
                        required
                        data-testid="input-message"
                      />
                    </div>
                    <Button type="submit" disabled={submitting} className="bg-[#1D2D2A] hover:bg-[#243D37]" data-testid="submit-ticket-btn">
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Submit Ticket
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Document Request Tab */}
            <TabsContent value="document" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Request a Document</CardTitle>
                  <CardDescription>
                    Request copies of documents or statements. Expected turnaround: 2-3 business days.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitDocRequest} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="doc_type">Document Type *</Label>
                        <Select 
                          value={docRequestForm.document_type} 
                          onValueChange={(value) => setDocRequestForm(prev => ({ ...prev, document_type: value }))}
                        >
                          <SelectTrigger data-testid="select-doc-type">
                            <SelectValue placeholder="Select document type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="participation_agreement">Participation Agreement</SelectItem>
                            <SelectItem value="cession_document">Cession Document</SelectItem>
                            <SelectItem value="statement_copy">Statement Copy</SelectItem>
                            <SelectItem value="tax_certificate">Tax Certificate</SelectItem>
                            <SelectItem value="bank_confirmation">Bank Confirmation Letter</SelectItem>
                            <SelectItem value="portfolio_summary">Portfolio Summary</SelectItem>
                            <SelectItem value="audit_certificate">Audit Certificate</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="doc_loan_ref">Related Loan Reference (optional)</Label>
                        <Input
                          id="doc_loan_ref"
                          value={docRequestForm.related_loan_reference}
                          onChange={(e) => setDocRequestForm(prev => ({ ...prev, related_loan_reference: e.target.value }))}
                          placeholder="e.g., PTC-2024-001"
                          data-testid="input-doc-loan-ref"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="doc_description">Description *</Label>
                      <Textarea
                        id="doc_description"
                        value={docRequestForm.description}
                        onChange={(e) => setDocRequestForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Please specify the document period, any special requirements, or additional details..."
                        rows={4}
                        required
                        data-testid="input-doc-description"
                      />
                    </div>
                    <Button type="submit" disabled={submitting} className="bg-[#1D2D2A] hover:bg-[#243D37]" data-testid="submit-doc-request-btn">
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <FileText className="h-4 w-4 mr-2" />
                          Submit Request
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* My Tickets */}
          {myTickets.length > 0 && (
            <Card data-testid="my-tickets-section">
              <CardHeader>
                <CardTitle className="text-lg">My Support Tickets</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {myTickets.slice(0, 5).map((ticket) => (
                    <div key={ticket.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{ticket.subject}</p>
                        <p className="text-xs text-gray-500">
                          {ticket.category} • {formatDate(ticket.created_at)}
                        </p>
                      </div>
                      {getStatusBadge(ticket.status)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Contact Info */}
        <div className="space-y-6">
          <Card data-testid="contact-info-section">
            <CardHeader>
              <CardTitle className="text-lg">Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-[#1D2D2A] flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Phone</p>
                  <p className="text-sm text-gray-600">{contactInfo?.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-[#1D2D2A] flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Email</p>
                  <a href={`mailto:${contactInfo?.email}`} className="text-sm text-[#1D2D2A] hover:underline">
                    {contactInfo?.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#1D2D2A] flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Address</p>
                  <p className="text-sm text-gray-600">
                    {contactInfo?.address?.line1}<br />
                    {contactInfo?.address?.line2}<br />
                    {contactInfo?.address?.city}, {contactInfo?.address?.province}<br />
                    {contactInfo?.address?.postal_code}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-[#1D2D2A] flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Business Hours</p>
                  <p className="text-sm text-gray-600">{contactInfo?.business_hours}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1D2D2A]/5 border-[#1D2D2A]/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-[#1D2D2A] flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm text-[#1D2D2A]">Response Time</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {contactInfo?.response_time}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Quick Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <a href="/kyc" className="block text-sm text-[#1D2D2A] hover:underline">
                Update KYC Documents →
              </a>
              <a href="/profile" className="block text-sm text-[#1D2D2A] hover:underline">
                Update Profile & Banking →
              </a>
              <a href="/tax" className="block text-sm text-[#1D2D2A] hover:underline">
                Download Tax Certificates →
              </a>
              <a href="/statements" className="block text-sm text-[#1D2D2A] hover:underline">
                View Statements →
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
